import { Button } from "@tellescope/ui";
import { Dialog } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import type { Form, FormResponse } from "@tellescope/types-models";
import { useOrganization } from "../../../shared/store/organizationStore";
import { buildFormCompletionURL } from "../../../config/portal";

interface PinnedFormDialogProps {
  /** The form to display */
  form: Form | null;
  /** Existing form response to continue (if any) */
  existingResponse: FormResponse | null;
  /** Access code for the form (prepared by parent) */
  accessCode: string | null;
  /** iframe authentication token (fetched by parent) */
  iframeToken: string | null;
  /** Whether the form is being prepared */
  isPreparingForm: boolean;
  /** Whether the iframe token is being loaded */
  isLoadingToken: boolean;
  /** Error from form preparation, if any */
  error: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for completing a pinned form.
 * Pure presentational component - receives all data as props from parent.
 */
export function PinnedFormDialog({
  form,
  existingResponse,
  accessCode,
  iframeToken,
  isPreparingForm,
  isLoadingToken,
  error,
  open,
  onOpenChange,
}: PinnedFormDialogProps) {
  const { t } = useTranslation("forms");
  const organization = useOrganization();

  // Compute loading state
  const isLoading = isPreparingForm || isLoadingToken;

  // Build the iframe URL from props provided by parent
  // Always uses subdomain-based URL for consistent embedding (not customPortalURL)
  const formCompletionURL =
    form && organization && iframeToken && accessCode
      ? buildFormCompletionURL({
          organization: { subdomain: organization.subdomain },
          formId: form.id,
          accessCode,
          authToken: iframeToken,
          hideBg: true,
          startAtNext: !!existingResponse, // Continue from where left off if existing response
        })
      : null;

  const formTitle = form?.displayTitle || form?.title || t("Form");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-lg h-[85vh] flex flex-col p-0 gap-0" closeOnInteractOutside={false}>
        {/* Header */}
        <Dialog.Header className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 md:hidden" onClick={() => onOpenChange(false)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Dialog.Title className="text-base font-medium truncate pr-8">{formTitle}</Dialog.Title>
          </div>
        </Dialog.Header>

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                <p className="text-sm">{t("Loading form...")}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-destructive">
                <p className="text-sm font-medium mb-1">{t("Error loading form")}</p>
                <p className="text-xs text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : formCompletionURL ? (
            <iframe
              src={formCompletionURL}
              className="w-full h-full border-0"
              title={formTitle}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-muted-foreground">
                <p className="text-sm">{t("Unable to load form")}</p>
              </div>
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
